import { getFileExtensionFromUrl } from '#pplx/frontend/common/files/urls.js';
import { ExtensionToMimeType } from '#pplx/frontend/components/utils/fileUploadUtils.js';
/**
 * Generates the appropriate ID format for a Microsoft picker item.
 *
 * @param item - The Microsoft Graph API picker item
 * @param connectorName - The connector type ('onedrive' or 'sharepoint')
 * @returns For SharePoint: JSON string with item_id, drive_id, and site_id. For OneDrive: the item_id
 */
const getMicrosoftItemId = (item, connectorName) => {
    if (item.parentReference.sharepointIds?.siteId && connectorName === 'sharepoint') {
        return JSON.stringify({
            item_id: item.id,
            drive_id: item.parentReference.driveId,
            site_id: item.parentReference.sharepointIds.siteId,
        });
    }
    return item.id;
};
/**
 * Converts a Microsoft Graph API file picker item to a Google Picker Document format.
 *
 * This utility exists to provide a consistent file representation across different
 * file picker implementations (Microsoft OneDrive/SharePoint and Google Drive).
 * By converting to the Google Picker Document format, downstream code can handle
 * files from any source uniformly.
 *
 * @param item - The Microsoft Graph API picker item to convert
 * @param connectorName - The connector type ('onedrive' or 'sharepoint')
 * @returns A Google Picker DocumentObject, or null if the file has no extension
 *
 * @remarks
 * - For SharePoint items, the ID is a JSON string containing item_id, drive_id, and site_id
 * - For OneDrive items, the ID is just the item_id
 * - Returns null if the file has no extension (cannot determine MIME type)
 *
 * @example
 * ```ts
 * const microsoftItem = {
 *   id: 'abc123',
 *   name: 'document.pdf',
 *   size: 1024,
 *   parentReference: {
 *     driveId: 'drive123',
 *     sharepointIds: { siteId: 'site123' }
 *   }
 * };
 *
 * const doc = microsoftItemToPickerDocument(microsoftItem, 'sharepoint');
 * // Returns:
 * // {
 * //   id: '{"item_id":"abc123","drive_id":"drive123","site_id":"site123"}',
 * //   name: 'document.pdf',
 * //   sizeBytes: 1024,
 * //   mimeType: 'application/pdf'
 * // }
 * ```
 */
export const microsoftItemToPickerDocument = (item, connectorName) => {
    const extension = getFileExtensionFromUrl(item.name);
    if (!extension)
        return null;
    return {
        id: getMicrosoftItemId(item, connectorName),
        name: item.name,
        sizeBytes: item.size,
        mimeType: ExtensionToMimeType[extension],
    };
};
/**
 * Converts a Dropbox file picker item to a Google Picker Document format.
 *
 * This utility provides consistent file representation across different file picker
 * implementations. By converting to the Google Picker Document format, downstream
 * code can handle files from any source uniformly.
 *
 * @param item - The Dropbox picker item to convert
 * @returns A Google Picker DocumentObject, or null if the file has no extension
 *
 * @remarks
 * - Returns null if the file has no extension (cannot determine MIME type)
 * - The ID is taken directly from the Dropbox item
 *
 * @example
 * ```ts
 * const dropboxItem = {
 *   id: 'id:abc123',
 *   name: 'document.pdf',
 *   bytes: 1024,
 *   link: 'https://...',
 *   icon: 'https://...',
 *   isDir: false,
 *   linkType: 'direct'
 * };
 *
 * const doc = dropboxItemToPickerDocument(dropboxItem);
 * // Returns:
 * // {
 * //   id: 'id:abc123',
 * //   name: 'document.pdf',
 * //   sizeBytes: 1024,
 * //   mimeType: 'application/pdf'
 * // }
 * ```
 */
export const dropboxItemToPickerDocument = (item) => {
    const extension = getFileExtensionFromUrl(item.name);
    if (!extension)
        return null;
    return {
        id: item.id,
        name: item.name,
        sizeBytes: item.bytes,
        mimeType: ExtensionToMimeType[extension],
    };
};
/**
 * Converts a Box file picker item to a Google Picker Document format.
 *
 * This utility provides consistent file representation across different file picker
 * implementations. By converting to the Google Picker Document format, downstream
 * code can handle files from any source uniformly.
 *
 * @param item - The Box picker item to convert
 * @returns A Google Picker DocumentObject
 *
 * @remarks
 * - Unlike Dropbox, Box picker items always have an extension field
 * - The ID is taken directly from the Box item
 * - The extension field includes the leading dot (e.g., '.pdf')
 *
 * @example
 * ```ts
 * const boxItem = {
 *   id: 'file_123',
 *   name: 'document.pdf',
 *   size: 1024,
 *   extension: '.pdf'
 * };
 *
 * const doc = boxItemToPickerDocument(boxItem);
 * // Returns:
 * // {
 * //   id: 'file_123',
 * //   name: 'document.pdf',
 * //   sizeBytes: 1024,
 * //   mimeType: 'application/pdf'
 * // }
 * ```
 */
export const boxItemToPickerDocument = (item) => {
    return {
        id: item.id,
        name: item.name,
        sizeBytes: item.size,
        mimeType: ExtensionToMimeType[item.extension],
    };
};
//# sourceMappingURL=toPickerDocument.js.map